import type { TFunction } from 'i18next';
import { resolvePlanTier } from '@/shared/ui';
import type { TrialUsage } from '@/shared/utils/trial-quota';
import type { PlanScopedLimits } from '@/features/pricing';
import type { TrialPlan } from './UsageMeters';

/** The slice of a catalogue plan the trial legend reads. */
interface CatalogPlan {
  title: string;
  limits?: PlanScopedLimits;
}

/**
 * The plan a trial previews, and what it gives per month (RN-06).
 *
 * The tier comes from the usage payload (`plan_tier` is the real tier, also
 * during the trial); the allowances come from the catalogue, so a republished
 * catalogue moves the legend without a code change.
 *
 * @param usage - The usage summary, or null.
 * @param plans - The catalogue plans for the agency's segment.
 * @param t - Translation function (features/pricing).
 * @returns The plan, or null outside the trial.
 */
export function trialPlanOf(
  usage: (TrialUsage & { plan_tier?: string }) | null | undefined,
  plans: CatalogPlan[],
  t: TFunction,
): TrialPlan | null {
  if (!usage?.trial_cap) return null;
  const tier = resolvePlanTier(usage.plan_tier);
  const seat = plans.find(
    (plan) => resolvePlanTier(plan.title) === tier && plan.limits?.seat,
  )?.limits?.seat;
  return {
    name: t(`subscription.trial.tier.${tier}`),
    quotas: {
      interaction: seat?.interactions ?? null,
      partnershipSearch: seat?.partnership_searches ?? null,
    },
  };
}
